import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import { ExpressionOfInterestPopupsGqlHelper } from '../../../../../dotcms/dotcms-models/popup/dotcms-expression-of-interest-popups';
import { useBuildersToProcessGqlData } from '../../../../../dotcms/use-builders-to-process-gql-data';
import { GlobalLoadingStateOperation } from '../../../global-loading-context/global-loading-context';
import { useFetchIssuerDotcmsContentWithDefaultFallback } from '../../../../../dotcms/use-fetch-from-dotcms-with-default-fallback';
import { useLocalStorage } from '../../../../../utils/storage/localstorage/use-local-storage';
import { LocalStorageKey } from '../../../../../utils/storage/localstorage/localstorage';
import { useGetExpressionOfInterestInfo } from '../../../../../hooks/use-get-expression-of-interest-info';

const useExpressionOfInterestPopups = () => {
  const [dialogOpen, setDialogOpen] = useState<boolean | null>();

  const { createGqlQueryForSingle, processGqlDataAsSingle, contentTypeName } =
    useBuildersToProcessGqlData(ExpressionOfInterestPopupsGqlHelper);
  const { data, loading, error } =
    useFetchIssuerDotcmsContentWithDefaultFallback({
      buildGqlSubQuery: createGqlQueryForSingle,
      processResponse: processGqlDataAsSingle,
      loadingStateOperationName:
        GlobalLoadingStateOperation.EXPRESSION_OF_NOTICE_POPUPS_FETCH,
      contentTypeName,
    });

  const {
    getValueFromLocalStorage: isEoiCompleted,
    setValueToLocalStorage: setEoiCompleted,
  } = useLocalStorage(LocalStorageKey.ARAMARK_EOI_CONSENT_COMPLETED);

  const { isBeforeEoiDeadline, isAfterEoiDeadline } =
    useGetExpressionOfInterestInfo();

  useEffect(() => {
    setDialogOpen(
      !isEoiCompleted()?.eoiConsentCompleted &&
        (isBeforeEoiDeadline || isAfterEoiDeadline),
    );
  }, [isEoiCompleted, isBeforeEoiDeadline, isAfterEoiDeadline]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, [setDialogOpen]);

  const handleAlreadySubmitted = useCallback(() => {
    setEoiCompleted({ eoiConsentCompleted: true });
    handleDialogClose();
  }, [handleDialogClose, setEoiCompleted]);

  return useMemo(
    () => ({
      dialogData: isAfterEoiDeadline
        ? data?.afterEoiDeadlineNotice
        : data?.beforeEnrollmentNotice,
      error,
      loading,
      dialogOpen,
      setDialogOpen,
      handleDialogClose,
      handleAlreadySubmitted,
      isAfterEoiDeadline,
    }),
    [
      data,
      isAfterEoiDeadline,
      error,
      loading,
      dialogOpen,
      setDialogOpen,
      handleDialogClose,
      handleAlreadySubmitted,
    ],
  );
};

export default useExpressionOfInterestPopups;
